import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type Unassigned, unassignedSymbol } from '~/utils'

type OrderedCustomerAttribute = {
  order: number
  name: string
}

export type OrderedCustomerAttributeSelectorProps<
  TOrderedCustomerAttribute extends OrderedCustomerAttribute,
> = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  customerAttributes: TOrderedCustomerAttribute[]
  isNullSelectable?: boolean
  nullText?: string
  onChange?: (order: TOrderedCustomerAttribute['order']) => void
  unsetText?: string
  value: TOrderedCustomerAttribute['order'] | Unassigned | null
}

export const OrderedCustomerAttributeSelector = React.forwardRef(
  <TCustomerAttribute extends OrderedCustomerAttribute>(
    {
      customerAttributes,
      isNullSelectable = false,
      nullText,
      onChange,
      unsetText,
      value: valueProp,
      ...rest
    }: OrderedCustomerAttributeSelectorProps<TCustomerAttribute>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const data = React.useMemo(
      () =>
        isNullSelectable
          ? [{ order: null, name: nullText }, ...customerAttributes]
          : customerAttributes,
      [customerAttributes, isNullSelectable, nullText],
    )
    const value = React.useMemo(
      () =>
        data.find(({ order }) => order === valueProp) ?? {
          order: unassignedSymbol,
          name: unsetText,
        },
      [data, unsetText, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (customerAttribute: TCustomerAttribute) => {
        onChange?.(customerAttribute.order)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={data}
        dataItemKey="order"
        textField="name"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
